import { Anchor, CircleButton, DialogButton, Switch, Typography, isLocale } from "@tosize/ui";
import { useEffect, useState } from "react";
import { FormattedList, FormattedMessage, useIntl } from "react-intl";
import { useConfig } from "../../../context/ConfigContext";
import { useErrorContext } from "../../../context/ErrorContext";
import { usePostDataContext } from "../../../context/PostDataContext";
import useFinishes from "../../../hooks/useFinishes";
import useShapes from "../../../hooks/useShapes";
import { ExplanationButton } from "../../ui/ExplanationButton";
import { WarningDialog } from "../Finishes/WarningDialog";
import { Finishes } from "../Steps/Finishes";

export function FinishesField() {
	const intl = useIntl();
	const { routes } = useConfig();
	const { shape } = useShapes();
	const { isDisabled, finishes, isLoading } = useFinishes();
	const { finishSwitchChecked, onFinishSwitchCheckedChange, workpiece } = usePostDataContext();
	const { hasErrors } = useErrorContext();
	const [showRemoveWarning, setShowRemoveWarning] = useState(false);

	const hasWorkpieceFinishes = Object.keys(workpiece.finishes.side).length > 0;

	useEffect(() => {
		if (
			(hasErrors || isLoading || !finishes?.length) &&
			!!finishSwitchChecked &&
			!hasWorkpieceFinishes
		) {
			onFinishSwitchCheckedChange(false);
		}
	}, [
		hasErrors,
		isLoading,
		finishes?.length,
		finishSwitchChecked,
		hasWorkpieceFinishes,
		onFinishSwitchCheckedChange,
	]);

	if (isDisabled) {
		return null;
	}

	return (
		<div>
			<Switch
				id="finishes-switch"
				disabled={hasErrors || isLoading || !finishes?.length}
				checked={finishSwitchChecked}
				onCheckedChange={(checked) => onFinishSwitchCheckedChange(checked)}
				className="justify-between"
				labelPosition="left"
				label={
					<>
						<ExplanationButton
							title={intl.formatMessage({ defaultMessage: "Afwerkingen", id: "bm3zxG" })}
						>
							{!!finishes?.length && (
								<DialogButton
									title={intl.formatMessage({
										defaultMessage: "Randafwerkingen op maat",
										id: "hqPgR+",
									})}
									content={
										<>
											<Typography>
												<FormattedMessage
													defaultMessage="Kies de afwerking die past bij jouw project"
													id="wDmHcS"
												/>
												:{" "}
												<FormattedList
													value={finishes.map((finish) => finish.label)}
													type="conjunction"
												/>
												{". "}
												{routes.get("customer_service/assortment/finishes") && (
													<>
														<FormattedMessage defaultMessage="Wil je meer weten?" id="PnV5I1" />{" "}
														<Anchor
															target="_blank"
															href={routes.get("customer_service/assortment/finishes")}
															className="inline"
														>
															<FormattedMessage
																defaultMessage="Bekijk onze uitgebreide uitleg over randafwerkingen."
																id="b7CAnY"
															/>
														</Anchor>
													</>
												)}
											</Typography>

											<br />

											{finishes.find((finish) => finish.abstract_code === "absband") && (
												<Typography>
													<FormattedMessage
														defaultMessage="Voeg je geen ABS-band toe? Geen probleem! Zonder ABS-band afwerking ontvang je afgewerkte zaagsnedes waarbij de binnenkant van het materiaal zichtbaar is."
														id="GIyYox"
													/>
												</Typography>
											)}
										</>
									}
								>
									<CircleButton component="button" className="text-sm" />
								</DialogButton>
							)}
						</ExplanationButton>

						<div className="mt-0.5 font-sans text-sm">
							{hasErrors ? (
								<span className="text-warning">
									<FormattedMessage
										defaultMessage="Vul eerst de afmetingen correct in"
										id="gd6RUY"
									/>
								</span>
							) : isLoading || !shape ? (
								<span className="text-primary-medium">
									<FormattedMessage defaultMessage="Afwerkingen worden geladen" id="9WzD6f" />
								</span>
							) : (
								<>
									{finishes && finishes.length > 0 ? (
										<span className="text-shades-dark">
											<FormattedList
												value={finishes?.map((finish, index) =>
													isLocale({
														current: intl.locale,
														pattern: "language",
														supported: ["de"],
													}) || index === 0
														? finish.label
														: finish.label.toLowerCase(),
												)}
												type="conjunction"
											/>
										</span>
									) : (
										<span className="text-primary-medium">
											<FormattedMessage
												defaultMessage="Voor deze vorm zijn geen afwerkingen beschikbaar"
												id="+FcGOX"
											/>
										</span>
									)}
								</>
							)}
						</div>
					</>
				}
			/>

			<WarningDialog
				open={showRemoveWarning}
				onOpenChange={setShowRemoveWarning}
				removeAction={() => onFinishSwitchCheckedChange(false)}
			/>

			{finishSwitchChecked && (
				<div className="mt-2">
					<Finishes />
				</div>
			)}
		</div>
	);
}
